import {
  Button,
  type ButtonProps,
  Divider,
  HStack,
  Heading,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { IconCheck, IconRightArrow } from '@/components/Icons';
import { CandidateListType } from '@/services/api';

type CandidateListTypeDropdownProps = {
  selectedListType?: CandidateListType;
  onListTypeChange: (listType: CandidateListType) => void;
  children?: React.ReactNode;
  menuButtonProps?: ButtonProps;
};

export const candidateListTypes = [
  { type: CandidateListType.AllCandidates, key: 'allCandidates' as const },
  { type: CandidateListType.LongList, key: 'longList' as const },
  { type: CandidateListType.ShortList, key: 'shortList' as const },
];

export const CandidateListTypeDropdown: React.FC<
  CandidateListTypeDropdownProps
> = ({ selectedListType, onListTypeChange, children, menuButtonProps }) => {
  const { t } = useTranslation(['candidate']);

  return (
    <Menu placement="bottom-start">
      {({ isOpen }) => (
        <>
          <MenuButton
            as={Button}
            variant="ghost"
            p={0}
            h="auto"
            w="full"
            _hover={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}
            _focus={{ boxShadow: 'none' }}
            rightIcon={
              <IconRightArrow
                transform={`${isOpen ? 'rotate(270deg)' : 'rotate(90deg)'}`}
                transition="0.3s"
                fontSize="sm"
                mr={4}
              />
            }
            bg="transparent"
            shadow="none"
            {...menuButtonProps}
          >
            <Heading fontSize="2xl" textAlign="left">
              {children}
            </Heading>
          </MenuButton>
          <MenuList
            minWidth="200px"
            overflow="hidden"
            shadow="md"
            borderRadius={12}
            alignItems="start"
            py={0}
          >
            <VStack divider={<Divider />} spacing={0} alignItems="stretch">
              {candidateListTypes.map(({ type, key }) => (
                <MenuItem
                  key={type}
                  onClick={() => onListTypeChange(type)}
                  color="gray.900"
                  _hover={{ bg: 'transparent' }}
                  _focus={{ bg: 'transparent' }}
                  _active={{ bg: 'transparent' }}
                >
                  <HStack justify="space-between" w="full">
                    <Text>{t(`candidate:${key}`)}</Text>
                    {selectedListType === type && (
                      <IconCheck fontSize="20px" color="gray.500" />
                    )}
                  </HStack>
                </MenuItem>
              ))}
            </VStack>
          </MenuList>
        </>
      )}
    </Menu>
  );
};
